import type { ThreadEnvMode } from "@cmmd-forge/contracts";

import type { ProjectDefaultModelPair } from "./projectDefaultModel";

/** Plain strings on purpose — see the note on `Checkout`. */
interface UpdatableMember {
  readonly environmentId: string;
  readonly id: string;
  readonly name: string;
  readonly defaultModelSelection?: ProjectDefaultModelPair | null;
  readonly defaultThreadEnvMode?: ThreadEnvMode | null;
}

/** The subset of `project.meta.update` a group settings page can change. */
export interface GroupMetaPatch {
  readonly title?: string;
  /**
   * `null` clears the project's default, letting each machine pick for itself.
   * Absent means this patch does not touch the field at all — a distinction
   * that has to survive, or a reset would plan nothing.
   */
  readonly defaultModelSelection?: ProjectDefaultModelPair | null;
  /**
   * Where this project's new threads start. `null` unpins it, restoring the
   * app-wide setting; absent means this patch does not touch the pin, so a
   * rename cannot silently unpin a project.
   */
  readonly defaultThreadEnvMode?: ThreadEnvMode | null;
}

export interface GroupMetaUpdate {
  readonly environmentId: string;
  readonly projectId: string;
  readonly patch: GroupMetaPatch;
}

export interface GroupMetaUpdatePlan {
  readonly updates: readonly GroupMetaUpdate[];
  /** The same shape, carrying each member's PRIOR value. */
  readonly rollback: readonly GroupMetaUpdate[];
}

function sameSelection(
  left: ProjectDefaultModelPair | null,
  right: ProjectDefaultModelPair | null,
): boolean {
  if (left === null || right === null) return left === right;
  // Both halves, not just the provider: a same-provider model change is the
  // shape of every mispairing this codebase has shipped, and comparing
  // providers alone would call it a no-op and keep the old model.
  return left.provider === right.provider && left.model === right.model;
}

/** Whether this member still differs from the patch on any field it touches. */
function isStale(member: UpdatableMember, patch: GroupMetaPatch): boolean {
  if (patch.title !== undefined && member.name !== patch.title) return true;
  if (patch.defaultThreadEnvMode !== undefined) {
    if ((member.defaultThreadEnvMode ?? null) !== patch.defaultThreadEnvMode) return true;
  }
  if (patch.defaultModelSelection === undefined) return false;
  return !sameSelection(member.defaultModelSelection ?? null, patch.defaultModelSelection);
}

/** The same patch shape, filled with what this member holds right now. */
function priorValues(member: UpdatableMember, patch: GroupMetaPatch): GroupMetaPatch {
  return {
    ...(patch.title === undefined ? {} : { title: member.name }),
    ...(patch.defaultModelSelection === undefined
      ? {}
      : { defaultModelSelection: member.defaultModelSelection ?? null }),
    ...(patch.defaultThreadEnvMode === undefined
      ? {}
      : { defaultThreadEnvMode: member.defaultThreadEnvMode ?? null }),
  };
}

/**
 * Plan a settings change across every checkout in a group.
 *
 * A group's name and its new-thread default are shared, so a change has to
 * reach every member — writing only the representative would leave the other
 * machines showing something else, and which one is "representative" is an
 * implementation detail the user never chose.
 *
 * `rollback` is captured UP FRONT rather than read back after a failure: once a
 * fan-out has partially applied, the prior values are already gone from the
 * store, and two machines disagreeing about a project's settings is exactly the
 * state that is hardest to notice and hardest to undo.
 */
export function planGroupMetaUpdate(
  members: readonly UpdatableMember[],
  patch: GroupMetaPatch,
): GroupMetaUpdatePlan {
  // Only the members that would actually change. A no-op write is not free:
  // every member emits `project.meta-updated`, so renaming one checkout in a
  // five-machine group would otherwise wake four connections to tell them
  // nothing changed. Mirrors t3code's `projectGroupTitleNeedsUpdate`.
  const stale = members.filter((member) => isStale(member, patch));

  return {
    updates: stale.map((member) => ({
      environmentId: member.environmentId,
      projectId: member.id,
      patch,
    })),
    // Scoped to what is actually written, or undoing would "restore" a value
    // onto a checkout this change never touched.
    rollback: stale.map((member) => ({
      environmentId: member.environmentId,
      projectId: member.id,
      patch: priorValues(member, patch),
    })),
  };
}
